import apiClient from './api';
import { AxiosResponse } from 'axios';

// Types
export interface DialogueMessage {
  speaker: 'character' | 'player' | 'narrator';
  character?: string;
  text: string;
  imageUrl?: string;
  delay?: number;
}

export interface ConversationQuestionOption {
  text: string;
  nextDialogueIndex: number;
  isCorrect: boolean;
  points: number;
  responseText?: string;
}

export interface ConversationQuestion {
  text: string;
  options: ConversationQuestionOption[];
  explanation?: string;
  bibleReference: {
    book: string;
    chapter: number;
    verses: string;
    text?: string;
  };
}

export interface DialogueItem {
  type: 'message' | 'question';
  message?: DialogueMessage;
  question?: ConversationQuestion;
}

export interface Conversation {
  _id: string;
  title: string;
  description: string;
  category: string;
  mainCharacter: string;
  difficulty: string;
  setting: {
    location: string;
    time: string;
    backgroundImageUrl?: string;
  };
  dialogue: DialogueItem[];
  timelineEvents: any[];
  completionRewards?: {
    points: number;
    achievements: any[];
  };
  estimatedDuration: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationProgress {
  _id: string;
  user: string;
  conversation: string;
  lastCompletedIndex: number;
  completed: boolean;
  score: number;
  answeredQuestions: {
    questionIndex: number;
    selectedOption: number;
    isCorrect: boolean;
    points: number;
    answeredAt: string;
  }[];
  lastInteraction: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationAnswer {
  conversationId: string;
  questionIndex: number;
  selectedOptionIndex: number;
}

const conversationService = {
  // Get all conversations with filters
  getConversations: async (
    filters: { 
      category?: string; 
      difficulty?: string; 
      character?: string;
      page?: number;
      limit?: number;
    } = {}
  ): Promise<AxiosResponse<{ 
    status: string; 
    data: { 
      conversations: Conversation[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        pages: number;
      }
    } 
  }>> => {
    return apiClient.get('/conversations', { params: filters });
  },

  // Get a specific conversation by ID
  getConversation: async (id: string): Promise<AxiosResponse<{ 
    status: string; 
    data: { conversation: Conversation } 
  }>> => {
    return apiClient.get(`/conversations/${id}`);
  },

  // Get random conversations
  getRandomConversations: async (
    filters: { 
      category?: string; 
      difficulty?: string; 
      count?: number;
      excludeIds?: string[];
    } = {}
  ): Promise<AxiosResponse<{ 
    status: string; 
    data: { conversations: Conversation[] } 
  }>> => {
    return apiClient.post('/conversations/random', filters);
  },

  // Get conversations by character
  getConversationsByCharacter: async (character: string): Promise<AxiosResponse<{ 
    status: string; 
    data: { conversations: Conversation[] } 
  }>> => {
    return apiClient.get(`/conversations/character/${character}`);
  },

  // Get user progress for a conversation
  getUserProgress: async (conversationId: string): Promise<AxiosResponse<{ 
    status: string; 
    data: { progress: ConversationProgress } 
  }>> => {
    return apiClient.get(`/conversations/${conversationId}/progress`);
  },

  // Update user progress
  updateProgress: async (
    conversationId: string, 
    data: { 
      lastCompletedIndex: number;
      completed?: boolean;
    }
  ): Promise<AxiosResponse<{ 
    status: string; 
    data: { progress: ConversationProgress } 
  }>> => {
    return apiClient.patch(`/conversations/${conversationId}/progress`, data);
  },

  // Submit an answer to a conversation question
  submitAnswer: async (answer: ConversationAnswer): Promise<AxiosResponse<{ 
    status: string; 
    data: { 
      isCorrect: boolean;
      points: number;
      progress: ConversationProgress;
    } 
  }>> => {
    return apiClient.post(`/conversations/${answer.conversationId}/answer`, {
      questionIndex: answer.questionIndex,
      selectedOption: answer.selectedOptionIndex
    });
  }
};

export default conversationService; 